/**
 * Hướng dẫn thêm app vào Màn hình chính, theo TỪNG TRÌNH DUYỆT trên iPhone.
 *
 * iOS chỉ cho web push khi app đã nằm ở Màn hình chính (từ iOS 16.4), và Apple
 * không có API nào để tự mở hộp thoại cài - chỉ thêm tay được. Nên hướng dẫn
 * đúng là thứ duy nhất làm được cho người dùng iPhone.
 *
 * Mỗi trình duyệt giấu nút đó một chỗ khác nhau, và Safari còn đổi chỗ giữa các
 * bản iOS: từ iOS 26 Apple chuyển nó ra sau nút ⋯ cạnh thanh địa chỉ, trước đó
 * nằm ở nút Chia sẻ dưới thanh công cụ. Chỉ sai một bước là người dùng bấm quanh
 * không ra rồi bỏ cuộc.
 *
 * File này KHÔNG import gì cả: toàn hàm thuần đọc User-Agent, nên kiểm được
 * từng chuỗi UA mà không phải dựng cả tầng gọi API.
 */

export type TrinhDuyetIOS = "safari" | "chrome" | "firefox" | "edge" | "opera" | "khac";

export interface HuongDanCai {
  /** Tên trình duyệt hiện cho người dùng đọc. */
  ten: string;
  trinhDuyet: TrinhDuyetIOS;
  buoc: string[];
  /**
   * Trình duyệt này thêm được shortcut, nhưng shortcut đó có nhận được thông báo
   * hay không thì không chắc - tài liệu của các bên nói khác nhau. Safari là
   * đường duy nhất chắc chắn, nên cảnh báo và chỉ luôn cách chuyển.
   */
  chuaChacNhanDuocThongBao: boolean;
}

/** Số hiệu iOS lấy từ User-Agent ("OS 26_0 like Mac OS X" -> 26). 0 nếu không đọc được. */
export const phienBanIOS = (ua: string = navigator.userAgent): number => {
  const m = ua.match(/OS (\d+)[._]/);

  return m ? Number(m[1]) : 0;
};

export const trinhDuyetIOS = (ua: string = navigator.userAgent): TrinhDuyetIOS => {
  if (/CriOS/.test(ua)) return "chrome";
  if (/FxiOS/.test(ua)) return "firefox";
  if (/EdgiOS/.test(ua)) return "edge";
  if (/OPiOS|OPT\//.test(ua)) return "opera";

  return "safari";
};

const BUOC_CUOI = "Mở Biadi từ biểu tượng vừa hiện trên màn hình, rồi bật lại Thông báo trận đấu";

export const huongDanCaiDat = (ua: string = navigator.userAgent): HuongDanCai => {
  const trinhDuyet = trinhDuyetIOS(ua);

  if (trinhDuyet === "safari") {
    /* Từ iOS 26, "Thêm vào MH chính" nằm sau nút ⋯ cạnh thanh địa chỉ, không còn
       ở nút Chia sẻ dưới đáy. Hộp thoại cũng có thêm công tắc "Mở như Web App" -
       tắt nó đi thì chỉ còn là một bookmark và KHÔNG nhận được thông báo. */
    if (phienBanIOS(ua) >= 26) {
      return {
        ten: "Safari",
        trinhDuyet,
        chuaChacNhanDuocThongBao: false,
        buoc: [
          "Bấm nút ⋯ bên phải thanh địa chỉ",
          "Chọn Chia sẻ trong menu vừa hiện ra",
          "Kéo xuống, chọn Thêm vào MH chính",
          "Giữ bật công tắc Mở như Web App, rồi bấm Thêm",
          BUOC_CUOI,
        ],
      };
    }

    return {
      ten: "Safari",
      trinhDuyet,
      chuaChacNhanDuocThongBao: false,
      buoc: [
        "Bấm nút Chia sẻ ở thanh dưới cùng",
        "Kéo xuống, chọn Thêm vào MH chính",
        "Bấm Thêm ở góc trên bên phải",
        BUOC_CUOI,
      ],
    };
  }

  /* Mỗi trình duyệt một bộ bước ĐẦY ĐỦ, không ghép tiền tố chung với đuôi chung:
     Firefox mở thẳng bảng Chia sẻ từ thanh địa chỉ, nên thêm một bước "Chọn Chia
     sẻ" nữa là thừa và mâu thuẫn - đúng lỗi đã đo được khi dùng khuôn chung. */
  const theoTrinhDuyet: Record<Exclude<TrinhDuyetIOS, "safari">, { ten: string; buoc: string[] }> = {
    chrome: {
      ten: "Chrome",
      buoc: [
        "Bấm nút ⋯ ở thanh công cụ của Chrome",
        "Chọn Chia sẻ…",
        "Kéo xuống, chọn Thêm vào MH chính",
        "Bấm Thêm",
      ],
    },
    firefox: {
      ten: "Firefox",
      buoc: [
        "Bấm biểu tượng Chia sẻ ở thanh địa chỉ (hoặc mở menu ☰ rồi chọn Chia sẻ)",
        "Kéo xuống, chọn Thêm vào MH chính",
        "Bấm Thêm",
      ],
    },
    edge: {
      ten: "Edge",
      buoc: [
        "Bấm nút ⋯ ở thanh công cụ của Edge",
        "Chọn Chia sẻ",
        "Kéo xuống, chọn Thêm vào MH chính",
        "Bấm Thêm",
      ],
    },
    opera: {
      ten: "Opera",
      buoc: [
        "Bấm nút menu của Opera ở thanh công cụ",
        "Chọn Chia sẻ",
        "Kéo xuống, chọn Thêm vào MH chính",
        "Bấm Thêm",
      ],
    },
    khac: {
      ten: "trình duyệt này",
      buoc: [
        "Mở menu của trình duyệt",
        "Tìm mục Chia sẻ",
        "Kéo xuống, chọn Thêm vào MH chính",
        "Bấm Thêm",
      ],
    },
  };
  const { ten, buoc } = theoTrinhDuyet[trinhDuyet];

  return {
    ten,
    trinhDuyet,
    chuaChacNhanDuocThongBao: true,
    buoc: [...buoc, BUOC_CUOI],
  };
};
